import { Button, Spinner } from '@heroui/react'
import type { ParsedBulkInviteError } from '@qse/api'
import { Alert, DropzoneComponent } from '@qse/ui'
import { formatStorageValue } from '@qse/utils'
import { Download, FileText, X } from 'lucide-react'

import { BULK_INVITE_ACCEPT, BULK_INVITE_MAX_FILE_BYTES } from './helpers'
import RowErrorsTable from './row-errors-table'
import { downloadBulkInviteTemplate } from './template'

interface UploadStepProps {
  fileName?: string
  error: ParsedBulkInviteError | null
  isInspecting: boolean
  onFileSelected: (file: File) => void
  onClearFile: () => void
  onContinue: () => void
  onCancel: () => void
  /** Re-upload replaces an existing request's file, so the copy and CTA differ. */
  isReupload?: boolean
}

export default function UploadStep({
  fileName,
  error,
  isInspecting,
  onFileSelected,
  onClearFile,
  onContinue,
  onCancel,
  isReupload = false,
}: UploadStepProps) {
  return (
    <div className="flex flex-col gap-5 py-5 w-full">
      <p className="text-sm text-gray-600 dark:text-[#AFAFAF]">
        {isReupload
          ? 'Upload a corrected file. It replaces this request and re-processes it — anyone already invited by it won’t be invited twice.'
          : 'Upload a CSV or Excel file to invite many users at once.'}{' '}
        Column names don’t have to match exactly — you’ll review how each one was read before
        anything is sent. Need a starting point?{' '}
        <button
          className="text-primary font-medium hover:underline"
          type="button"
          onClick={downloadBulkInviteTemplate}
        >
          Download the template
        </button>
        . Up to 1,000 rows, max {formatStorageValue(BULK_INVITE_MAX_FILE_BYTES)}.
      </p>

      {fileName ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-primary shrink-0" />
            <span className="text-sm font-medium truncate">{fileName}</span>
          </div>
          <Button
            isIconOnly
            isDisabled={isInspecting}
            radius="full"
            size="sm"
            variant="light"
            onPress={onClearFile}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <DropzoneComponent
          acceptedFileTypes={BULK_INVITE_ACCEPT}
          allowFolders={false}
          maxFiles={1}
          maxSize={BULK_INVITE_MAX_FILE_BYTES}
          onFilesAccepted={files => {
            if (files[0]) onFileSelected(files[0])
          }}
        />
      )}

      {isInspecting && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3">
          <Spinner size="sm" />
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Reading your file’s columns… Please keep this dialog open until it finishes.
          </p>
        </div>
      )}

      {error && (
        <div className="flex flex-col gap-3">
          <Alert
            color="danger"
            title={error.message}
            description={
              error.fieldErrors.length > 0
                ? 'See the details below, then try again.'
                : 'Fix the issue and try again, or download the template to check the format.'
            }
          />
          <RowErrorsTable errors={error.fieldErrors} />
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-3 w-full">
        <Button
          radius="sm"
          startContent={<Download className="w-4 h-4" />}
          variant="flat"
          onPress={downloadBulkInviteTemplate}
        >
          Template
        </Button>

        <div className="flex gap-3">
          <Button isDisabled={isInspecting} radius="sm" variant="flat" onPress={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            isDisabled={!fileName}
            isLoading={isInspecting}
            radius="sm"
            onPress={onContinue}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  )
}
